import { Site } from '@envato/foundation-types'
import * as React from 'react'
import Helmet from 'react-helmet'

import { config } from '../../../common/config'
import siteConfigs from '../../../config/sites'

interface IProps {
  seoMeta: any
  location: any
  currentSite: Site
  noIndex: boolean
  baseUrl: string
  canonicalPath: string
}

export default ({
  seoMeta,
  currentSite,
  noIndex,
  baseUrl,
  canonicalPath
}: IProps) => {
  const title = seoMeta.title || ''
  const description = seoMeta.meta_description || seoMeta.description || ''
  const siteConfig = siteConfigs[currentSite]

  // Defining meta tags
  const meta = [
    { name: 'description', content: description },
    { property: 'og:title', content: title },
    { property: 'og:description', content: description }
  ]

  // set no index header
  if (noIndex) {
    meta.push({ name: 'robots', content: 'noindex, nofollow' })
  }

  meta.push({ property: 'og:url', content: baseUrl + canonicalPath })
  // Defining link tags
  const link: React.AnchorHTMLAttributes<HTMLAnchorElement>[] = [
    { rel: 'canonical', href: baseUrl + canonicalPath }
  ]
  const homePageOrganizationSchema = {
    type: 'application/ld+json',
    innerHTML: `{
        "@context": "https://schema.org",
        "@type": "Organization",
        "name": "Envato",
        "alternateName": "Envato Market",
        "URL": "${baseUrl}",
        "logo": "https://static.tutsplus.com/assets/envato-tutsplus-dark-1f19ad23dc41961ab1f1116daeb9c104.png",
        "contactPoint": {
          "@type": "ContactPoint",
          "telephone": "+61 3 8376 6284",
          "contactType": "corporate contact"
        },
        "sameAs": [
          "https://tutsplus.com/",
          "https://business.tutsplus.com/",
          "https://code.tutsplus.com/",
          "https://design.tutsplus.com/",
          "https://webdesign.tutsplus.com/",
          "https://photography.tutsplus.com/",
          "https://music.tutsplus.com/",
          "https://cgi.tutsplus.com/",
          "https://gamedevelopment.tutsplus.com/",
          "https://computers.tutsplus.com/",
          "https://elements.envato.com/",
          "https://themeforest.net/",
          "https://codecanyon.net/",
          "https://audiojungle.net/",
          "https://videohive.net/",
          "https://graphicriver.net/",
          "https://photodune.net/",
          "https://3docean.net/",
          "https://placeit.net/",
          "https://www.twenty20.com/",
          "https://mixkit.co/",
          "https://www.reshot.com/",
          "https://milkshake.app/",
          "https://twitter.com/envato",
          "https://www.facebook.com/envato",
          "https://www.youtube.com/user/Envato",
          "https://www.instagram.com/envato/",
          "https://www.pinterest.com.au/envato/"
        ], "subOrganization": [
          {
            "@type": "Organization",
            "name": "Envat.com",
            "url": "https://envato.com/"
          },
          {
            "@type": "Organization",
            "name": "Envato Elements",
            "url": "https://elements.envato.com"
          },
          {
            "@type": "Organization",
            "name": "Envato Market",
            "url": "https://market.envato.com"
          },
          {
            "@type": "Organization",
            "name": "Placeit by Envato",
            "url": "https://placeit.net/"
          },
          {
            "@type": "Organization",
            "name": "Envato Tuts+",
            "url": "https://tutsplus.com"
          },
          {
            "@type": "Organization",
            "name": "Milkshake",
            "url": "https://milkshake.app/"
          }
        ]
      }`
  }

  const organizationSchema = {
    type: 'application/ld+json',
    innerHTML: `{
        "@context": "http://schema.org",
        "@type": "Organization",
        "name": "${siteConfig.name}",
        "url": "${baseUrl}",
        "sameAs": [
          "https://www.facebook.com/envato",
          "https://twitter.com/envato",
          "https://www.youtube.com/user/Envato/",
          "https://www.instagram.com/envato/",
          "https://www.pinterest.com/envato/"
        ],
        "logo": "${config.assets.PUBLIC_PATH}/images/logo-envato-market-black.svg"
      }`
  }
  // Define extra scripts
  const script = [
    {
      type: 'application/ld+json',
      innerHTML: `{
        "@context": "http://schema.org",
        "@type": "WebSite",
        "name": "${siteConfig.name}",
        "url": "${baseUrl}"
      }`
    }
  ]

  if (canonicalPath == '/') {
    script.push(homePageOrganizationSchema)
  } else {
    script.push(organizationSchema)
  }

  return (
    <Helmet
      encodeSpecialCharacters={true}
      title={title}
      defaultTitle="Envato Market"
      meta={meta}
      link={link}
      script={script}
    />
  )
}
